<script setup>
import { ref, computed, onMounted } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3';
import { useAiRequest } from '@/composables/useAiRequest';
import { aiErrorMessage } from '@/composables/aiErrorMessage';
import AppAiState from '@/Components/AppAiState.vue';
import AppReportInaccurate from '@/Components/AppReportInaccurate.vue';
import DragIcon from '@/Components/DragIcon.vue';

// Quotesheet AI — "Suggested products" for this lead. Rules-based matcher; fetches
// the cached match on mount (cheap, no compute on page load) and recomputes on
// demand via Refresh. Degrades elegantly via useAiRequest + AppAiState. Shown only
// when the quote-match feature is enabled for the agency.
const props = defineProps({
    lead: { type: Object, required: true },
    draggable: { type: Boolean, default: false }, // shows the drag handle when used as a right-pane widget
});

const enabled = computed(() => usePage().props.value?.features?.['quote-match'] === true);
const leadId = computed(() => props.lead?.id);

const products = ref([]);
const asOf = ref(null);
const loaded = ref(false);
const semantic = ref(false);

const { loading, error, retryable, run } = useAiRequest();

const reportContent = computed(() =>
    products.value.map((p) => `${p.title} (${p.score})`).join(', '),
);

function tone(score) {
    if (score >= 75) return 'bg-emerald-100 text-emerald-700';
    if (score >= 50) return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-600';
}

async function apply(res) {
    if (!res) return;
    products.value = res.data.products ?? [];
    asOf.value = res.data.generated_at ?? null;
    semantic.value = res.data.semantic === true;
    loaded.value = true;
}

async function load() {
    if (!leadId.value) return;
    apply(await run(() => window.axios.get(route('leads.ai.quote-match', leadId.value))));
}

async function refresh() {
    if (loading.value || !leadId.value) return;
    apply(await run(() => window.axios.post(route('leads.ai.quote-match.refresh', leadId.value))));
}

const building = ref(false);

async function buildDraft() {
    if (building.value || !leadId.value) return;
    building.value = true;
    try {
        const { data } = await window.axios.post(route('leads.ai.quote-match.build', leadId.value));
        if (data?.redirect) {
            window.location.href = data.redirect; // into the quote-sheet builder
            return;
        }
        building.value = false;
    } catch (e) {
        window.$notify?.error?.({ title: aiErrorMessage(e, 'Could not build the quote sheet.') });
        building.value = false;
    }
}

onMounted(() => {
    if (enabled.value) load();
});
</script>

<template>
    <div v-if="enabled" class="intro-y bg-white rounded-lg shadow">
        <div class="border-b p-3 flex space-x-4 items-center justify-between rounded-t-lg bg-tb-widget-header">
            <div class="flex items-center gap-x-3 gap-y-0 flex-wrap">
                <h4 class="text-xl text-white font-black">Suggested Products</h4>
                <span v-if="semantic" class="rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold text-white" title="Ordering refined by AI (semantic similarity)">AI-ranked</span>
                <span v-if="asOf" class="text-xs text-white/80">as of {{ asOf }}</span>
            </div>

            <div class="flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded bg-white px-3 py-1.5 text-xs font-bold text-tb-blue shadow-sm hover:bg-slate-100 disabled:opacity-60"
                    :disabled="loading"
                    @click="refresh"
                >
                    <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M10 3a7 7 0 1 0 6.32 4h-2.2A5 5 0 1 1 10 5V3Z" /><path d="M13 1.5 16.5 5 13 8.5V1.5Z" />
                    </svg>
                    {{ loading ? 'Matching…' : 'Refresh' }}
                </button>
                <DragIcon v-if="draggable" class="menu-icon move-icon w-6 h-6 cursor-move text-white -mr-1" />
            </div>
        </div>

        <div class="p-4 space-y-3">
            <AppAiState :error="error" :retryable="retryable" @retry="refresh" />

            <template v-if="products.length">
                <ul class="space-y-2">
                    <li
                        v-for="p in products"
                        :key="p.product_id"
                        class="flex items-start gap-3 rounded-md border border-slate-100 p-2.5"
                    >
                        <span :class="['mt-0.5 shrink-0 rounded-md px-2 py-1 text-sm font-bold', tone(p.score)]">{{ p.score }}</span>
                        <div class="min-w-0">
                            <p class="text-sm font-semibold text-gray-900">
                                {{ p.title }}
                                <span v-if="p.category" class="ml-1 text-xs font-normal text-slate-400">· {{ p.category }}</span>
                            </p>
                            <div v-if="p.reasons?.length" class="mt-1 flex flex-wrap gap-1.5">
                                <span
                                    v-for="(r, i) in p.reasons"
                                    :key="i"
                                    class="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                                >{{ r }}</span>
                            </div>
                        </div>
                    </li>
                </ul>

                <button
                    type="button"
                    class="w-full rounded-md bg-tb-blue px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-tb-blue/90 disabled:opacity-60"
                    :disabled="building"
                    @click="buildDraft"
                >
                    {{ building ? 'Building…' : 'Build draft quote sheet' }}
                </button>
                <p class="text-center text-[11px] text-slate-400">Creates a draft you can tweak &amp; price — nothing is sent.</p>

                <div class="flex justify-end border-t pt-2">
                    <AppReportInaccurate feature="quote_match" :lead-id="leadId" subject="Suggested products" :content="reportContent" />
                </div>
            </template>

            <p v-else-if="loaded && !error" class="text-sm text-slate-500">
                No matching products yet — add data points (age/income/state) to your quotesheet products, then click “Refresh”.
            </p>
        </div>
    </div>
</template>
